import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { db } from "../../../firebaseFunctions/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Loader2 } from "lucide-react";

const Toggle = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
    } ${checked ? "bg-gray-900" : "bg-gray-300"}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const NOTIFICATION_TYPES = [
  {
    id: "applicationStatusUpdates",
    label: "Application status updates",
    description: "When your application is approved or denied",
    channels: ["email", "sms"],
  },
  {
    id: "paymentReceipts",
    label: "Payment receipts",
    description: "Receipt emails after paying a registration fee",
    channels: ["email"],
  },
  {
    id: "paymentReminders",
    label: "Payment reminders",
    description: "Reminders when a registration fee is due",
    channels: ["email", "sms"],
  },
  {
    id: "boothAssignments",
    label: "Booth assignments",
    description: "When the organizer assigns your booth number",
    channels: ["email", "sms"],
  },
  {
    id: "eventReminders",
    label: "Event day reminders",
    description: "48-hour reminder before an event you're registered for",
    channels: ["email", "sms"],
  },
  {
    id: "documentRequests",
    label: "Document requests",
    description: "When an organizer requests documents from you",
    channels: ["email", "sms"],
  },
  {
    id: "eventAnnouncements",
    label: "Event announcements",
    description: "Updates and announcements from event organizers",
    channels: ["email", "sms"],
  },
  {
    id: "eventCancellations",
    label: "Event cancellations",
    description: "When an event you're registered for is cancelled",
    channels: ["email", "sms"],
    required: true,
  },
];

const DEFAULT_PREFS = {
  emailEnabled: true,
  smsEnabled: true,
  applicationStatusUpdates: { email: true, sms: true },
  paymentReceipts: { email: true, sms: false },
  paymentReminders: { email: true, sms: true },
  boothAssignments: { email: true, sms: true },
  eventReminders: { email: true, sms: true },
  documentRequests: { email: true, sms: true },
  eventAnnouncements: { email: true, sms: false },
  eventCancellations: { email: true, sms: true },
};

const Notifications = () => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [profile, setProfile] = useState({ email: "", phoneNumber: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!user?.uid) return;
      try {
        const [prefsSnap, profileSnap] = await Promise.all([
          getDoc(doc(db, "notificationSettings", user.uid)),
          getDoc(doc(db, "MPS_BusinessProfiles", user.uid)),
        ]);
        setPrefs(prefsSnap.exists() ? { ...DEFAULT_PREFS, ...prefsSnap.data() } : DEFAULT_PREFS);
        if (profileSnap.exists()) {
          setProfile({
            email: profileSnap.data().email || profileSnap.data().businessEmail || "",
            phoneNumber: profileSnap.data().phoneNumber || profileSnap.data().businessPhoneNumber || "",
          });
        }
      } catch (err) {
        console.error("Failed to load notification prefs", err);
        setPrefs(DEFAULT_PREFS);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid]);

  const save = async (updated) => {
    if (!user?.uid) return;
    setSaving(true);
    setSaved(false);
    try {
      await setDoc(doc(db, "notificationSettings", user.uid), updated, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error("Failed to save notification prefs", err);
    } finally {
      setSaving(false);
    }
  };

  const toggleGlobal = (channel) => {
    const key = channel === "email" ? "emailEnabled" : "smsEnabled";
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    save(updated);
  };

  const toggleType = (typeId, channel) => {
    const updated = {
      ...prefs,
      [typeId]: { ...prefs[typeId], [channel]: !prefs[typeId]?.[channel] },
    };
    setPrefs(updated);
    save(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-8">
      {/* Global channel toggles */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Notification channels</h2>
          <p className="text-sm text-gray-500 mt-0.5">Master controls — turning off a channel disables all notifications for it</p>
        </div>
        <div className="divide-y divide-gray-100">
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Email</p>
              <p className="text-xs text-gray-500 mt-0.5">{profile.email || "No email on file"}</p>
            </div>
            <Toggle checked={prefs.emailEnabled} onChange={() => toggleGlobal("email")} />
          </div>
          <div className="flex items-center justify-between px-6 py-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Text message (SMS)</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {profile.phoneNumber || (
                  <span className="text-amber-600">No phone number on file — add one in Profile settings</span>
                )}
              </p>
            </div>
            <Toggle
              checked={prefs.smsEnabled}
              onChange={() => toggleGlobal("sms")}
              disabled={!profile.phoneNumber}
            />
          </div>
        </div>
      </div>

      {/* Per-type preferences */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Notification types</h2>
          <p className="text-sm text-gray-500 mt-0.5">Choose how you receive each type of notification</p>
        </div>
        <div className="divide-y divide-gray-100">
          {NOTIFICATION_TYPES.map((type) => {
            const typePref = prefs[type.id] || {};
            return (
              <div key={type.id} className="px-6 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{type.label}</p>
                      {type.required && (
                        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">Required</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    {type.channels.includes("email") && (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-400">Email</span>
                        <Toggle
                          checked={!!typePref.email}
                          onChange={() => toggleType(type.id, "email")}
                          disabled={type.required || !prefs.emailEnabled}
                        />
                      </div>
                    )}
                    {type.channels.includes("sms") && (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-xs text-gray-400">SMS</span>
                        <Toggle
                          checked={!!typePref.sms}
                          onChange={() => toggleType(type.id, "sms")}
                          disabled={type.required || !prefs.smsEnabled || !profile.phoneNumber}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Save status */}
      {(saving || saved) && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {saving ? "Saving..." : "✓ Saved"}
        </div>
      )}
    </div>
  );
};

export default Notifications;
